import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatError } from "@/lib/api";
import { SERVICE_TYPES } from "@/lib/ui";
import { Lightbulb } from "lucide-react";

const inputCls = "w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#2E7D6B] outline-none";

export default function FamilyDraftForm({ token, seekerName, drafts = [], onCreated }) {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", service_type: "errands", description: "", scheduled_date: tomorrow, scheduled_time: "10:00", location: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/family/drafts`, form, { headers: { "X-Family-Token": token } });
      toast.success(`Suggestion sent — ${seekerName.split(" ")[0]} just needs to tap Yes.`);
      setForm({ ...form, title: "", description: "" });
      onCreated?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="family-draft" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 mt-10">
      <div className="flex items-center gap-2"><Lightbulb className="w-6 h-6 text-[#D99B26]" /><h2 className="text-2xl font-display font-bold text-slate-900">Suggest a visit</h2></div>
      <p className="text-slate-600 mt-1 mb-5">Draft a request for {seekerName.split(" ")[0]}. Nothing is booked until they tap one big <strong>Yes</strong> on their Kindred home screen.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className="block font-semibold text-slate-800 mb-1">What's needed</label><input data-testid="fd-title" required value={form.title} onChange={set("title")} placeholder="e.g. Pick up blood-pressure tablets" className={inputCls} /></div>
        <div><label className="block font-semibold text-slate-800 mb-1">Type</label>
          <select data-testid="fd-type" value={form.service_type} onChange={set("service_type")} className={inputCls}>{SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
        <div><label className="block font-semibold text-slate-800 mb-1">Place (optional)</label><input data-testid="fd-location" value={form.location} onChange={set("location")} placeholder="Defaults to their home" className={inputCls} /></div>
        <div><label className="block font-semibold text-slate-800 mb-1">Date</label><input data-testid="fd-date" type="date" required value={form.scheduled_date} onChange={set("scheduled_date")} className={inputCls} /></div>
        <div><label className="block font-semibold text-slate-800 mb-1">Time</label><input data-testid="fd-time" type="time" required value={form.scheduled_time} onChange={set("scheduled_time")} className={inputCls} /></div>
        <div className="sm:col-span-2"><label className="block font-semibold text-slate-800 mb-1">Notes for the helper (optional)</label><textarea data-testid="fd-notes" rows={2} value={form.description} onChange={set("description")} className={inputCls + " py-3"} /></div>
        <button data-testid="fd-submit" disabled={saving} className="sm:col-span-2 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg">{saving ? "Sending…" : `Send suggestion to ${seekerName.split(" ")[0]}`}</button>
      </form>
      {drafts.length > 0 && (
        <ul data-testid="fd-list" className="mt-6 divide-y divide-[#E2D9CE]">
          {drafts.map((d) => (
            <li key={d.id} data-testid={`fd-item-${d.id}`} className="py-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-slate-800"><strong>{d.title}</strong> · {(d.scheduled_at || "").replace("T", " at ")}{d.helper_name ? ` · ${d.helper_name}` : ""}</span>
                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${d.status === "draft" ? "bg-[#FFF4EC] text-[#A9741A]" : "bg-[#E9F3F0] text-[#1F5F4F]"}`}>{d.status === "draft" ? "Waiting for Yes" : d.status === "matched" ? "Helper booked" : "Approved & posted"}</span>
              </div>
              <NoteLine draft={d} token={token} onSaved={onCreated} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NoteLine({ draft, token, onSaved }) {
  const [text, setText] = useState(draft.family_note?.text || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/family/notes`, { request_id: draft.id, text }, { headers: { "X-Family-Token": token } });
      toast.success(draft.helper_name ? `Note sent to ${draft.helper_name}` : "Note saved — the helper will see it when they accept");
      setEditing(false); onSaved?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!editing) return (
    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
      {draft.family_note?.text && <span data-testid={`fd-note-${draft.id}`} className="text-slate-600 italic">“{draft.family_note.text}”</span>}
      {draft.family_note?.reply && <span data-testid={`fd-note-reply-${draft.id}`} className="text-[#1F5F4F] font-semibold">↳ {draft.family_note.reply.from}: “{draft.family_note.reply.text}”</span>}
      <button type="button" data-testid={`fd-note-btn-${draft.id}`} onClick={() => setEditing(true)} className="text-[#2E7D6B] font-semibold underline underline-offset-4 min-h-[36px]">{draft.family_note?.text ? "Edit note" : "Add a note for the helper"}</button>
    </div>
  );
  return (
    <div className="mt-2 flex flex-col sm:flex-row gap-2">
      <input data-testid={`fd-note-input-${draft.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="e.g. Dad's tablets are at the Apollo counter; he walks slowly — no rush." className={inputCls + " flex-1"} />
      <button type="button" data-testid={`fd-note-save-${draft.id}`} disabled={saving || !text.trim()} onClick={save} className="bg-[#2E7D6B] disabled:opacity-50 text-white font-semibold px-5 min-h-[52px] rounded-xl">{saving ? "Sending…" : "Send note"}</button>
    </div>
  );
}
